import { FormJourneyItemLogic, LogicConditionOperation } from "@plura/shared/types";

const normalizeValue = (value: string | string[]): string => {
  if (Array.isArray(value)) {
    // Sort array to ensure consistent comparison regardless of order
    return value.slice().sort().join(',');
  }
  return value;
};

const evaluateCondition = (
  operation: LogicConditionOperation,
  value: string | null,
  userResponse: string | string[] | null | undefined,
): boolean => {
  if (value === null) return false;

  // The source question may be unanswered (skipped branch, deleted block).
  // Positive matches can't hold without a response; negative ones hold
  // vacuously. Without this, the string operations below throw.
  if (userResponse === undefined || userResponse === null) {
    return operation === 'IS_NOT_EQUAL_TO' || operation === 'DOES_NOT_CONTAIN';
  }

  // For CONTAINS and DOES_NOT_CONTAIN with arrays, check if value exists in array
  if (Array.isArray(userResponse) && (operation === 'CONTAINS' || operation === 'DOES_NOT_CONTAIN')) {
    const containsValue = userResponse.includes(value);
    return operation === 'CONTAINS' ? containsValue : !containsValue;
  }

  // Normalize both values for comparison (handles array to sorted comma-separated string)
  const normalizedUserValue = normalizeValue(userResponse);
  const normalizedExpectedValue = normalizeValue(value);

  switch (operation) {
    case 'IS_EQUAL_TO':
      return normalizedExpectedValue === normalizedUserValue;
    case 'IS_NOT_EQUAL_TO':
      return normalizedExpectedValue !== normalizedUserValue;
    case 'CONTAINS':
      return normalizedUserValue.includes(normalizedExpectedValue);
    case 'BEGINS_WITH':
      return normalizedUserValue.startsWith(normalizedExpectedValue);
    case 'ENDS_WITH':
      return normalizedUserValue.endsWith(normalizedExpectedValue);
    case 'DOES_NOT_CONTAIN':
      return !normalizedUserValue.includes(normalizedExpectedValue);
    default:
      return false;
  }
};

export const evaluateLogicItem = (
  logicItem: FormJourneyItemLogic,
  userResponses: Record<string, string | string[]>,
): boolean => {
  if (logicItem.conditionType === 'ALWAYS') {
    return true;
  }

  const conditions = logicItem.conditions ?? [];

  // Ensure all conditions must be true for AND logic
  const result = conditions.every(condition => {
    const userResponse = userResponses[condition.sourceBlockId];
    const conditionResult = evaluateCondition(
      condition.conditionOperation,
      condition.value,
      userResponse,
    );

    return conditionResult;
  });

  return result;
};